import { exception_rows, register_rows } from './compliance_policies_data';

const policy_defaults = {
  'Procurement policy': { effective_date: '01 Apr 2024', summary: 'Governs sourcing, thresholds, quotation handling and board-approved purchasing discipline.' },
  'Data protection policy': { effective_date: '12 Jun 2024', summary: 'Sets out lawful processing, retention, data-subject rights and breach handling.' },
  'Conflict of interest policy': { effective_date: '-', summary: 'Requires declaration and management of personal interests that could conflict with company decisions.' },
  'Delegation of authority policy': { effective_date: '01 Jul 2025', summary: 'Defines approval thresholds and decision rights for every approval.' },
  'Information security policy': { effective_date: '15 Jan 2025', summary: 'Defines acceptable use, access control, change management and incident response.' },
  'Code of conduct': { effective_date: '01 Mar 2024', summary: 'Sets behavioural standards for integrity, respect, anti-bribery and reporting obligations.' },
  'Risk management policy': { effective_date: '-', summary: 'Establishes the enterprise risk framework, appetite and reporting.' },
  'Travel & expense policy': { effective_date: '01 Feb 2025', summary: 'Rules for business travel, class of travel, per-diems, approvals and expense claims.' },
  'Supplier management policy': { effective_date: '-', summary: 'Lifecycle management of suppliers: onboarding, due diligence, monitoring and offboarding.' }
};

export const exception_status_class_map = {
  Approved: 'bg-success-subtle text-success-emphasis',
  Pending: 'bg-warning-subtle text-warning-emphasis',
  Expired: 'bg-danger-subtle text-danger-emphasis',
  Rejected: 'bg-secondary-subtle text-secondary-emphasis'
};

export const policy_status_class_map = {
  Draft: 'bg-info-subtle text-info-emphasis',
  'Under review': 'bg-warning-subtle text-warning-emphasis',
  Approved: 'bg-success-subtle text-success-emphasis',
  Archived: 'bg-secondary-subtle text-secondary-emphasis'
};

export const policy_review_class_map = {
  Scheduled: 'bg-info-subtle text-info-emphasis',
  'Due soon': 'bg-warning-subtle text-warning-emphasis',
  Overdue: 'bg-danger-subtle text-danger-emphasis',
  'Not issued': 'bg-secondary-subtle text-secondary-emphasis'
};

export const deep_clone = (value) => JSON.parse(JSON.stringify(value));

function value_or_default(value, fallback_value) {
  const resolved_value = value || fallback_value;
  return resolved_value;
}

function policy_default_values(policy) {
  const defaults = Reflect.get(policy_defaults, policy) || { effective_date: '-', summary: '' };
  return defaults;
}

function default_approval_stage(row) {
  if (row.status === 'Approved') return '4 - Complete';
  if (row.status === 'Under review') return '2 - Executive approval';
  return '0 - Owner draft';
}

function board_approval_required_value(row) {
  const requires_board = ['Finance', 'POPIA', 'Risk', 'Ethics'].includes(row.category);
  return requires_board ? 'Yes' : 'No';
}

function default_review_status(row) {
  if (row.review === 'Not issued') return 'Not issued';
  if (row.status === 'Under review') return 'Due soon';
  return 'Scheduled';
}

function prepared_policy_row(row, index) {
  const defaults = policy_default_values(row.policy);
  const policy_row = {
    ...row,
    id: value_or_default(row.id, `policy-${index + 1}`),
    owner: value_or_default(row.owner, 'TBC'),
    approver: value_or_default(row.approver, 'Board'),
    approval_stage: value_or_default(row.approval_stage, default_approval_stage(row)),
    board_approval_required: value_or_default(row.board_approval_required, board_approval_required_value(row)),
    review_status: value_or_default(row.review_status, default_review_status(row)),
    effective_date: value_or_default(row.effective_date, defaults.effective_date),
    summary: value_or_default(row.summary, defaults.summary)
  };
  return policy_row;
}

export function prepare_register_rows(rows) {
  const prepared_rows = rows.map((row, index) => prepared_policy_row(row, index));
  return prepared_rows;
}

export function prepare_exception_rows(rows) {
  const prepared_rows = rows.map((row, index) => ({ ...row, id: row.id || `exception-${index + 1}` }));
  return prepared_rows;
}

export const initial_policy_sequence = register_rows.length;
export const initial_exception_sequence = exception_rows.length;

export const create_empty_policy_form = () => ({
  policy: '',
  category: 'Finance',
  version: '',
  owner: '',
  approver: 'Board',
  status: 'Draft',
  approval_stage: '0 - Owner draft',
  board_approval_required: 'Yes',
  review_status: 'Scheduled',
  next_review_date: '',
  effective_date: '',
  summary: ''
});

function policy_form_defaults(row) {
  const defaults = {
    policy: value_or_default(row?.policy, ''),
    category: value_or_default(row?.category, 'Finance'),
    version: value_or_default(row?.version, ''),
    owner: value_or_default(row?.owner, ''),
    approver: value_or_default(row?.approver, 'Board'),
    status: value_or_default(row?.status, 'Draft'),
    approval_stage: value_or_default(row?.approval_stage, '0 - Owner draft'),
    board_approval_required: value_or_default(row?.board_approval_required, 'Yes'),
    review_status: value_or_default(row?.review_status, 'Scheduled')
  };
  return defaults;
}

export function create_policy_form_from_row(row) {
  const defaults = policy_form_defaults(row);
  const next_review_date = row?.review === 'Not issued' ? '' : value_or_default(row?.review, '');
  const policy_form = {
    ...defaults,
    next_review_date,
    effective_date: value_or_default(row?.effective_date, ''),
    summary: value_or_default(row?.summary, '')
  };
  return policy_form;
}

export const create_empty_exception_form = () => ({
  exception: '',
  policy: 'Procurement policy',
  requested_by: '',
  approved_by: '',
  expires: '',
  status: 'Approved'
});

export function create_exception_form_from_row(row) {
  const exception_form = {
    exception: value_or_default(row?.exception, ''),
    policy: value_or_default(row?.policy, 'Procurement policy'),
    requested_by: value_or_default(row?.requested_by, ''),
    approved_by: value_or_default(row?.approved_by, ''),
    expires: value_or_default(row?.expires, ''),
    status: value_or_default(row?.status, 'Approved')
  };
  return exception_form;
}

export const normalize_policy_approval_stage = (value, options) => {
  const normalized = String(value || '').replace(/\s+[—-]\s+/g, ' - ').trim();
  return options.includes(normalized) ? normalized : '0 - Owner draft';
};

export function resolve_exception_status_class(status) {
  const status_class = Reflect.get(exception_status_class_map, status) ?? exception_status_class_map.Approved;
  return status_class;
}

export function resolve_policy_status_class(status) {
  const status_class = Reflect.get(policy_status_class_map, status) ?? policy_status_class_map.Draft;
  return status_class;
}

export function resolve_policy_review_class(status) {
  const review_class = Reflect.get(policy_review_class_map, status) ?? policy_review_class_map.Scheduled;
  return review_class;
}
